import { SpacerComponent } from '@components/Spacer';
import SimpleArrowDownIconComponent from '@components/icons/simpleArrowDownIcon';
import Navbar from '@components/web-landingpage/NavBar';
import { DetailedComparisonTableComponent } from '@components/web/pricing/DetailedComparisonTable';
import FeaturedByCompaniesComponent from '@components/web/pricing/FeaturedByCompanies';
import PricingPageHeaderComponent from '@components/web/pricing/Header';
import { PlanComparisonComponent } from '@components/web/pricing/PlanComparison';
import SubscribeFAQSectionComponent from '@components/web/subscribe/SubscribeFAQSection';
import SubscribeFreeTrialExplanationComponent from '@components/web/subscribe/SubscribeFreeTrialExplanation';
import SubscribeTestimonialComponent from '@components/web/subscribe/SubscribeTestimonial';
import { TextLink } from '@components/web/texts/Texts';
import { StripePrices } from '@customtypes/subscription';
import { PricingPageSubscriptionInterval } from '@pages/pro';
import { getStripePrices, getStripePricesMax } from '@requests/subscriptions';
import { useQuery } from '@tanstack/react-query';
import { formatCurrency } from '@utils/currency-utils';
import React, { useState } from 'react';
import PricingIntervalSwitcherComponent from './PricingIntervalSwitcher';
import PricingBannerComponent from './PricingBanner';

export default function PricingPageComponent() {
  const [interval, setInterval] =
    useState<PricingPageSubscriptionInterval>('annually');

  const { data: stripePricesPro, isLoading: isLoadingPro } = useQuery({
    queryKey: ['stripe_prices_pro'],
    queryFn: () => getStripePrices(),
  });
  const { data: stripePricesMax, isLoading: isLoadingMax } = useQuery({
    queryKey: ['stripe_prices_max'],
    queryFn: () => getStripePricesMax(),
  });

  const planComparisonState: PlanComparisonState =
    isLoadingPro || isLoadingMax
      ? { type: 'loading' }
      : stripePricesPro && stripePricesMax
        ? calculatePlanComparisonState(stripePricesPro, stripePricesMax)
        : { type: 'error' };

  function scrollDetailedTableIntoView() {
    const element = document.getElementById('detailed-comparison-table');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }

  // FIXME: to prevent discrepancies, we are removing this display of the recurring discount from the pricing page for the ab test period
  // const specialOfferWindow = isUserInSpecialDiscountOfferWindow();
  const specialOfferWindow: any = undefined;

  return (
    <div className="flex min-h-screen flex-col items-center">
      <Navbar></Navbar>
      <div className="absolute left-0 top-0 z-[-1] h-[300px] w-[300px] overflow-hidden bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-[#8d60e266] to-[#25274600] to-[50%] sm:h-[600px] sm:w-[600px]"></div>
      <div className="absolute right-0 top-[250px] z-[-1] h-[300px] w-[300px] overflow-hidden bg-[radial-gradient(circle_at_100%_50%,_var(--tw-gradient-stops))] from-[#8d60e266] to-[#25274600] to-[45%] sm:h-[800px] sm:w-[800px]"></div>
      <Confetti></Confetti>
      <PricingBannerComponent
        specialOfferWindow={specialOfferWindow}
      ></PricingBannerComponent>
      <section className="mx-auto flex max-w-[1100px] flex-col items-center px-4 text-product2-content-primary">
        <SpacerComponent className="h-[120px]"></SpacerComponent>
        <PricingPageHeaderComponent></PricingPageHeaderComponent>
        <SpacerComponent className="h-20"></SpacerComponent>
        <PricingIntervalSwitcherComponent
          planComparisonState={planComparisonState}
          interval={interval}
          setInterval={setInterval}
        ></PricingIntervalSwitcherComponent>
        <SpacerComponent className="h-6"></SpacerComponent>
        <PlanComparisonComponent
          planComparisonState={planComparisonState}
          interval={interval}
        ></PlanComparisonComponent>
        <button
          className="mt-6 hidden items-center space-x-2 md:flex"
          onClick={() => scrollDetailedTableIntoView()}
        >
          <SimpleArrowDownIconComponent className="h-6 w-6"></SimpleArrowDownIconComponent>
          <TextLink className="whitespace-pre">See all features</TextLink>
        </button>
        <SpacerComponent className="h-[120px]"></SpacerComponent>
        <SubscribeFreeTrialExplanationComponent></SubscribeFreeTrialExplanationComponent>
        <SpacerComponent className="h-[120px]"></SpacerComponent>
        <FeaturedByCompaniesComponent></FeaturedByCompaniesComponent>
        <SpacerComponent className="hidden h-20 md:block"></SpacerComponent>
        <DetailedComparisonTableComponent
          id="detailed-comparison-table"
          planComparisonState={planComparisonState}
          interval={interval}
          className="hidden scroll-mt-20 md:block"
        ></DetailedComparisonTableComponent>
        <SpacerComponent className="h-[120px]"></SpacerComponent>
        <SubscribeTestimonialComponent></SubscribeTestimonialComponent>
        <SpacerComponent className="h-[120px]"></SpacerComponent>
        <div className="max-w-[720px]">
          <SubscribeFAQSectionComponent
            isEligibleForFreeTrialMax={true}
          ></SubscribeFAQSectionComponent>
        </div>
        <SpacerComponent className="h-[240px]"></SpacerComponent>
      </section>
    </div>
  );
}

function Confetti() {
  return (
    <div className="hidden lg:block">
      <div className="absolute top-[160px] z-[-1] h-[60px] w-[60px] translate-x-[400px] bg-product2-background-tertiary"></div>
      <div className="absolute top-[370px] z-[-1] h-[60px] w-[60px] -translate-x-[300px] bg-product2-background-secondary"></div>
      <div className="absolute top-[800px] z-[-1] h-[60px] w-[60px] -translate-x-[300px] bg-product2-background-tertiary"></div>
      <div className="absolute top-[1250px] z-[-1] h-[60px] w-[60px] -translate-x-[500px] bg-product2-background-secondary"></div>
      <div className="absolute top-[3400px] z-[-1] h-[60px] w-[60px] translate-x-[470px] bg-product2-background-secondary"></div>
      <div className="absolute top-[3460px] z-[-1] h-[60px] w-[60px] translate-x-[410px] bg-product2-background-tertiary"></div>
    </div>
  );
}

interface StripePriceData {
  currencyCode: string;
  monthlyPrice: string;
  monthlyPriceWithDiscount: string;
  yearlyPricePerMonth: string;
  yearlyPriceWithDiscountPerMonth: string;
  percentDecrease: number;
}

export type PlanComparisonState =
  | { type: 'loading' }
  | { type: 'error' }
  | {
      type: 'success';
      data: {
        stripePricesPro: StripePriceData;
        stripePricesMax: StripePriceData;
      };
    };

function calculatePlanComparisonState(
  stripePricesPro: StripePrices,
  stripePricesMax: StripePrices,
): PlanComparisonState {
  // FIXME: to prevent discrepancies, we are removing this display of the recurring discount from the pricing page for the ab test period
  // const specialOfferWindow = isUserInSpecialDiscountOfferWindow();
  const specialOfferWindow: any = undefined;

  const discountMultiplier = !specialOfferWindow
    ? 1
    : 1 - specialOfferWindow.discountPercent / 100;

  return {
    type: 'success',
    data: {
      stripePricesPro: {
        currencyCode: stripePricesPro.currencyCode,
        monthlyPrice: formatCurrency(
          stripePricesPro.monthlyPrice / 100,
          stripePricesPro.currencyCode,
        ),
        monthlyPriceWithDiscount: formatCurrency(
          Math.floor(stripePricesPro.monthlyPrice * discountMultiplier) / 100,
          stripePricesPro.currencyCode,
        ),
        // NOTE: yearly price broken down to monthly price for ab test
        yearlyPricePerMonth: formatCurrency(
          stripePricesPro.yearlyPrice / 100 / 12,
          stripePricesPro.currencyCode,
        ),
        yearlyPriceWithDiscountPerMonth: formatCurrency(
          Math.floor(stripePricesPro.yearlyPrice * discountMultiplier) /
            100 /
            12,
          stripePricesPro.currencyCode,
        ),
        percentDecrease: Math.floor(
          ((stripePricesPro.monthlyPrice * 12 - stripePricesPro.yearlyPrice) /
            (stripePricesPro.monthlyPrice * 12)) *
            100,
        ),
      },
      stripePricesMax: {
        currencyCode: stripePricesMax.currencyCode,
        monthlyPrice: formatCurrency(
          stripePricesMax.monthlyPrice / 100,
          stripePricesMax.currencyCode,
        ),
        monthlyPriceWithDiscount: formatCurrency(
          Math.floor(stripePricesMax.monthlyPrice * discountMultiplier) / 100,
          stripePricesMax.currencyCode,
        ),
        // NOTE: yearly price broken down to monthly price for ab test
        yearlyPricePerMonth: formatCurrency(
          stripePricesMax.yearlyPrice / 100 / 12,
          stripePricesMax.currencyCode,
        ),
        yearlyPriceWithDiscountPerMonth: formatCurrency(
          Math.floor(stripePricesMax.yearlyPrice * discountMultiplier) /
            100 /
            12,
          stripePricesMax.currencyCode,
        ),
        percentDecrease: Math.floor(
          ((stripePricesMax.monthlyPrice * 12 - stripePricesMax.yearlyPrice) /
            (stripePricesMax.monthlyPrice * 12)) *
            100,
        ),
      },
    },
  };
}
